/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Center pane — sequential per-phase task cards for the Task Builder.
 */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Task, TASK_TYPES, PHASES, PhaseId } from '../types';
import { taskDepth, isMilestoneUid, computeEstimate } from '../model';

function fmtHours(h: number) { return h < 1 ? `${Math.round(h * 60)} min` : `${h.toFixed(2)} hr`; }

export interface TaskCanvasProps {
  templateName: string; version: string;
  tasks: Task[]; // valid/active only
  activePhase: PhaseId; onPhaseChange: (p: PhaseId) => void;
  selectedUid: string | null; onSelect: (uid: string) => void;
  onAddTask: (phase: PhaseId) => void; onAddChild: (parentUid: string) => void;
  onDelete: (uid: string) => void; onDuplicate: (uid: string) => void;
  onInsertAfter: (afterUid: string) => void;   // new sibling slotted right after this task, not nested under it
}

function TaskCard({ t, tasks, byOrder, selected, onSelect, onDelete, onDuplicate, onAddChild, onInsertAfter }: {
  t: Task; tasks: Task[]; byOrder: Map<number, Task>; selected: boolean;
  onSelect: (u: string) => void; onDelete: (u: string) => void; onDuplicate: (u: string) => void; onAddChild: (u: string) => void;
  onInsertAfter: (u: string) => void;
}) {
  const depth = taskDepth(t.task_uid);
  const typeMeta = TASK_TYPES.find(tt => tt.value === t.taskType);
  const milestone = isMilestoneUid(tasks, t.task_uid);
  const isSystem = !!typeMeta?.system;
  return (
    <Box onClick={() => onSelect(t.task_uid)} data-testid={`task-card-${t.task_uid}`}
      sx={{ ml: depth * 2.75, bgcolor: milestone ? (theme => alpha(theme.palette.primary.main, 0.08)) : 'background.paper',
        border: 2, borderColor: selected ? 'primary.main' : 'divider', borderRadius: 2, p: 1.5, mb: 1, cursor: 'pointer' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: milestone ? 'primary.main' : 'grey.100', color: milestone ? '#fff' : 'text.secondary',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 700, flexShrink: 0, fontFamily: 'monospace' }}>{t.task_uid}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{t.taskName}</Typography>
            {milestone && <Chip size="small" color="primary" label="Milestone" />}
            {isSystem && <Chip size="small" color="success" label="System" />}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
            {typeMeta?.value && <Chip size="small" variant="outlined" label={typeMeta.label} />}
            <Chip size="small" icon={<AccessTimeIcon />} label={fmtHours(milestone ? computeEstimate(tasks, t.task_uid).hours : t.estimatedHours)} />
            {t.resources.map((r, i) => <Chip key={i} size="small" color="info" variant="outlined" label={`${r.group} ×${r.count}`} />)}
            {t.actions.length > 0 && <Chip size="small" color="secondary" label={`${t.actions.length} action${t.actions.length > 1 ? 's' : ''}`} />}
            {t.checklistItems.length > 0 && <Chip size="small" color="warning" variant="outlined" label={`${t.checklistItems.length} checklist item${t.checklistItems.length > 1 ? 's' : ''}`} />}
          </Box>
          {t.dependencies.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>depends on:</Typography>
              {t.dependencies.map(dep => {
                const d = byOrder.get(dep);
                const cls = t.dependency_classes?.[dep] ?? 'HARD';
                return (
                  <Chip key={dep} size="small" variant="outlined" color={cls === 'SOFT' ? 'info' : 'error'}
                    label={d ? d.taskName : `#${dep}`}
                    sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                );
              })}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <IconButton size="small" title="Add sub-task" onClick={() => onAddChild(t.task_uid)}><AddIcon fontSize="small" /></IconButton>
          {/* A phase root (depth 0, e.g. "1") is never duplicable, deletable, or a valid insertion
              anchor — the 4 phases must always exist. Duplicating/inserting-after it would mint a
              second, disconnected top-level "phase" entry; deleting it would cascade-delete the
              entire phase (see deleteTask). */}
          {depth > 0 && <IconButton size="small" title="Insert task after" onClick={() => onInsertAfter(t.task_uid)}><PlaylistAddIcon fontSize="small" /></IconButton>}
          {depth > 0 && <IconButton size="small" title="Duplicate" onClick={() => onDuplicate(t.task_uid)}><ContentCopyIcon fontSize="small" /></IconButton>}
          {depth > 0 && <IconButton size="small" title="Delete" onClick={() => onDelete(t.task_uid)}><DeleteOutlineIcon fontSize="small" /></IconButton>}
        </Box>
      </Box>
    </Box>
  );
}

/** Center pane: sequential task cards per phase. */
export default function TaskCanvas({ templateName, version, tasks, activePhase, onPhaseChange, selectedUid, onSelect, onAddTask, onAddChild, onDelete, onDuplicate, onInsertAfter }: TaskCanvasProps) {
  const list = useMemo(() => tasks.filter(t => t.phase === activePhase).sort((a, b) => a.task_order - b.task_order), [tasks, activePhase]);
  // Sum leaves only — a parent's hours are the (derived) sum of its children, so including both would double-count.
  const totalHours = useMemo(() => tasks.filter(t => !isMilestoneUid(tasks, t.task_uid)).reduce((s, t) => s + (t.estimatedHours || 0), 0), [tasks]);
  const byOrder = useMemo(() => new Map(tasks.map(t => [t.task_order, t])), [tasks]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      <Box sx={{ px: 3, pt: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>{templateName}</Typography>
          <Chip label={version} />
          <Typography variant="caption" color="text.secondary">{tasks.length} tasks · {totalHours.toFixed(1)} hrs total</Typography>
        </Box>
        <Tabs value={activePhase} onChange={(_, v) => onPhaseChange(v)}>
          {PHASES.map(p => {
            const n = tasks.filter(t => t.phase === p.id).length;
            return <Tab key={p.id} value={p.id} label={`${p.label} (${n})`} />;
          })}
        </Tabs>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
        {list.length === 0 ? (
          <Box onClick={() => onAddTask(activePhase)} sx={{ p: 5, textAlign: 'center', color: 'text.secondary', border: '2px dashed', borderColor: 'divider', borderRadius: 2, cursor: 'pointer' }}>
            <Typography fontWeight={600}>No tasks in this phase yet</Typography>
            <Typography variant="caption">Click to add the first task</Typography>
          </Box>
        ) : (
          <>
            {list.map(t => (
              <TaskCard key={t.task_uid} t={t} tasks={tasks} byOrder={byOrder} selected={t.task_uid === selectedUid}
                onSelect={onSelect} onDelete={onDelete} onDuplicate={onDuplicate} onAddChild={onAddChild}
                onInsertAfter={onInsertAfter} />
            ))}
            <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => onAddTask(activePhase)} sx={{ mt: 1 }}>
              Add task to {PHASES.find(p => p.id === activePhase)?.label}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
